<script setup lang="ts">
import {
  PopoverArrow,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
  SwitchRoot,
  SwitchThumb,
} from "reka-ui";
import { VList } from "virtua/vue";
import { useRouter } from "vue-router";
import { CollapsibleTrigger } from "reka-ui";
import { computed, ref, watch, useTemplateRef } from "vue";

import {
  getMarkStatus,
  isHighlightAnnotation,
  isRectangleAnnotation,
  isTranscriptAnnotation,
} from "@/services/UtilitiesService";
import { isRedaction } from "@/services/AnnotationService";

import { useBrandStore } from "@/stores/brand-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import SummaryItem from "@/components/CasePanels/Summary/SummaryItem.vue";
import SummaryRejectMark from "@/components/CasePanels/Summary/SummaryRejectMark.vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { Evidence } from "@/models/Evidence";
import type { Snapshot } from "@/models/Annotation";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const router = useRouter();
const routeParams = useCaseRouteParams();

const allFilteredRecords = ref(true);
const categoryOpen = ref(false);
const showRejectedMarks = ref(false);

const hasScrolledToInitial = ref(false);
const visibleRange = ref({ start: 0, end: 0 });
const virtualListRef = useTemplateRef<InstanceType<typeof VList> | null>("virtualListRef");

const selectedThemes = computed(() => useCaseFileStore().selectedThemes);

const highlightColors = computed(() => useBrandStore().viewerConfig.evidenceThemes?.highlightColors ?? {});

const filteredFilesInCurrentFolder = computed(() =>
  allFilteredRecords.value || !routeParams.value.casefile
    ? new Set(useMetadataStore().filteredFilesInCurrentFolder.map((f) => f.fileReference))
    : new Set([routeParams.value.casefile]),
);

const bookmarkCategoriesWithEvidences = computed(() => useEvidenceStore().availableCategoriesWithEvidences);

const allSnapshots = computed(() => {
  const files = new Map(useMetadataStore().filteredFiles.map((file, index) => [file.fileReference, index]));

  const bookmarks = useEvidenceStore().evidence.reduce(
    (acc, e) => {
      acc[e.value] ??= [];
      if (e.type === "annotation") acc[e.value].push(e);

      return acc;
    },
    {} as Record<string, Evidence[]>,
  );

  return useAnnotationStore()
    .annotations.flatMap<Snapshot>(({ markId: uid, fileReference, parsedMarkData, ...other }) => {
      if (parsedMarkData == null || isRedaction(parsedMarkData) || !files.has(fileReference)) return [];

      const base = {
        uid,
        fileReference,
        status: getMarkStatus(other),
        bookmarks: bookmarks[uid] ?? [],
        associatedThemes:
          other.evidenceThemes?.flatMap((e) => ({ theme: e.evidenceTheme, rejected: /reject/i.test(e.status) })) ?? [],
      };

      if (isTranscriptAnnotation(parsedMarkData)) {
        return {
          ...base,
          type: "TranscriptAnnotation",
          utteranceList: parsedMarkData.utteranceList,
          startTime: parsedMarkData.startTime,
          endTime: parsedMarkData.endTime,
        };
      }

      if (isRectangleAnnotation(parsedMarkData)) {
        return {
          ...base,
          type: "RectangleAnnotation",
          rectangle: parsedMarkData.rectangle,
          page: { ...parsedMarkData.pageData, number: parsedMarkData.pageNumber },
          childReference: other.supportingFileEntryFileReference,
          revision: "revision" in parsedMarkData ? parsedMarkData.revision : 0,
          text: other.metadata?.text ? [other.metadata.text].flat() : [],
          context: other.metadata?.context ? [other.metadata.context].flat() : [],
        };
      }

      if (isHighlightAnnotation(parsedMarkData)) {
        const linesY = parsedMarkData.lineGroups[0].lines.map((l) => l.y);
        const linesX = parsedMarkData.lineGroups[0].lines.map((l) => l.x);

        return {
          ...base,
          type: "HighlightAnnotation",
          uid: parsedMarkData.uid,
          revision: parsedMarkData.revision || 0,
          childReference: other.supportingFileEntryFileReference,
          text: other.metadata?.text ? [other.metadata.text].flat() : [],
          context: other.metadata?.context ? [other.metadata.context].flat() : [],
          page: { ...parsedMarkData.lineGroups[0].pageData, number: parsedMarkData.pageNumber },
          rectangle: {
            x: Math.min(...linesX),
            y: Math.min(...linesY),
            width: Math.max(...linesX) - Math.min(...linesX),
            height: Math.max(...linesY) - Math.min(...linesY),
          },
        };
      }

      return [];
    })
    .sort((a, b) => {
      // Sort by File Reference, already confirmed the map has the file
      if (a.fileReference !== b.fileReference) return files.get(a.fileReference)! - files.get(b.fileReference)!;

      // Group similar types together
      if (a.type !== b.type) return a.type.localeCompare(b.type);

      // Sort by start time
      if (a.type === "TranscriptAnnotation" && b.type === "TranscriptAnnotation") return a.startTime - b.startTime;

      const typedA = a as Exclude<Snapshot, { type: "TranscriptAnnotation" }>;
      const typedB = b as Exclude<Snapshot, { type: "TranscriptAnnotation" }>;

      // Sort by Page Number, then Y coordinate, then X coordinate
      return (
        typedA.page.number - typedB.page.number ||
        typedA.rectangle.y - typedB.rectangle.y ||
        typedA.rectangle.x - typedB.rectangle.x
      );
    });
});

const rejectMarkKey = computed(() => {
  void useAnnotationStore().annotations;
  return Math.random().toString(36);
});

const allCategoriesSelected = computed(
  () => selectedThemes.value == null || selectedThemes.value.length === bookmarkCategoriesWithEvidences.value.length,
);

const snapshots = computed<Snapshot[]>(() => {
  const evidencesInSelectedThemes = new Set<string>();

  for (const { entries } of allCategoriesSelected.value
    ? useEvidenceStore().categoriesWithEvidences
    : useEvidenceStore().categoriesWithEvidences.filter((c) => selectedThemes.value!.includes(c.name))) {
    for (const evidence of entries) evidencesInSelectedThemes.add(evidence.value);
  }

  const REJECTED_STATUS_RE = /rejected/i;
  return allSnapshots.value.filter(
    (s) =>
      (showRejectedMarks.value || !REJECTED_STATUS_RE.test(s.status)) &&
      filteredFilesInCurrentFolder.value.has(s.fileReference) &&
      evidencesInSelectedThemes.has(s.uid),
  );
});

const isSelectedOutOfView = computed(() => {
  const id = routeParams.value.annotation;
  if (id == null) return false;

  const snapshotIdx = snapshots.value.findIndex((snapshot) => snapshot.uid === id);
  if (snapshotIdx < 0) return false;

  return snapshotIdx < visibleRange.value.start || snapshotIdx > visibleRange.value.end;
});

watch(
  [() => routeParams.value.annotation, virtualListRef],
  () => {
    scrollToSelected(hasScrolledToInitial.value ? "nearest" : "center");
    hasScrolledToInitial.value = true;
  },
  { immediate: true },
);

function onScrollEnd() {
  if (virtualListRef.value == null) return;

  const newStart = virtualListRef.value.findStartIndex();
  const newEnd = virtualListRef.value.findEndIndex();

  if (newStart === visibleRange.value.start && newEnd === visibleRange.value.end) return;
  visibleRange.value = { start: newStart, end: newEnd };
}

function scrollToSelected(align: "center" | "nearest") {
  const id = routeParams.value.annotation;
  if (id == null || virtualListRef.value == null) return;

  const snapshotIdx = snapshots.value.findIndex((snapshot) => snapshot.uid === id);
  if (snapshotIdx < 0) return;

  virtualListRef.value.scrollToIndex(snapshotIdx, { align });
}

function goToAnnotation(fileReference: string, annotationId: string) {
  router.push({
    name: "casefileannotation",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: fileReference,
      annotation: annotationId,
    },
    force: true,
  });
}

const toggleCategory = (category: string | boolean) => useCaseFileStore().setSelectedThemes(category);
</script>
<template>
  <div class="tw-flex tw-size-full tw-h-full tw-flex-col tw-overflow-y-auto">
    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1 tw-bg-gray-800 tw-p-1">
      <PopoverRoot v-model:open="categoryOpen">
        <PopoverTrigger
          class="tw-btn-md-gray"
          :disabled="bookmarkCategoriesWithEvidences.length < 1"
          :aria-label="categoryOpen ? 'Hide Theme Selection' : 'Select Themes'"
        >
          {{ categoryOpen ? "Hide Theme Selection" : "Select Themes" }}
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent side="bottom" align="start" :as-child="true" :align-offset="3">
            <ul
              class="tw-b-r tw-z-[100] tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-max-w-xs tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow"
            >
              <li class="tw-flex tw-min-h-0 tw-shrink-0 tw-gap-2 tw-py-1">
                <input
                  id="toggle_selectAll"
                  type="checkbox"
                  class="tw-shrink-0"
                  :checked="allCategoriesSelected"
                  @change="toggleCategory(($event.target as HTMLInputElement).checked)"
                />
                <label for="toggle_selectAll" class="tw-flex-1 tw-cursor-pointer tw-select-none tw-text-sm">
                  Select all
                </label>
              </li>
              <li
                v-for="{ name, entries } in bookmarkCategoriesWithEvidences"
                :key="name"
                class="tw-flex tw-min-h-0 tw-shrink-0 tw-gap-2 tw-py-1"
              >
                <input
                  v-for="selected in [selectedThemes == null || selectedThemes.includes(name)]"
                  :id="`toggle_${name}`"
                  :key="selected.toString()"
                  type="checkbox"
                  class="tw-shrink-0"
                  :checked="selected"
                  :aria-label="`Toggle to ${selected ? 'Hide' : 'Show'} ${name}`"
                  @change="() => toggleCategory(name)"
                />
                <label :for="`toggle_${name}`" class="tw-flex-1 tw-cursor-pointer tw-select-none tw-text-sm">
                  <span
                    class="tw-mr-1 tw-w-full tw-rounded-sm tw-px-1"
                    :style="{ 'background-color': highlightColors[name] ?? 'inherit' }"
                  >
                    &nbsp;
                  </span>
                  {{ name }} [{{ entries.length }}]
                </label>
              </li>
            </ul>
            <PopoverArrow class="tw-fill-white" />
          </PopoverContent>
        </PopoverPortal>
      </PopoverRoot>
      <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-p-1">
        <label for="rejectedMarks" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">
          Show rejected marks
        </label>
        <SwitchRoot
          id="rejectedMarks"
          v-model="showRejectedMarks"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
      </div>
      <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-p-1">
        <label for="summaryFilteredRecords" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">
          All Filtered Records
        </label>
        <SwitchRoot
          id="summaryFilteredRecords"
          v-model="allFilteredRecords"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
          :disabled="routeParams.casefile == null"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
      </div>
      <div class="tw-flex-grow tw-self-center tw-text-right tw-text-white">
        Showing {{ snapshots.length }} of {{ allSnapshots.length }}
      </div>
    </div>
    <div
      v-if="snapshots.length > 0"
      id="case-summary"
      class="content-visibility-auto summary-container tw-relative tw-h-full"
    >
      <VList
        ref="virtualListRef"
        v-slot="{ item, index }: { item: Snapshot; index: number }"
        class="tw-size-full tw-overflow-auto tw-px-4 tw-py-3"
        :data="snapshots"
        :overscan="15"
        @scroll-end="onScrollEnd"
      >
        <SummaryItem
          :key="`${item.uid}-${item.status}-${item.type}`"
          :snapshot="item"
          :is-selected="routeParams.annotation === item.uid"
          :class="{ 'tw-mt-3': index > 0 }"
          @click="goToAnnotation(item.fileReference, item.uid)"
        >
          <template #headerActions="{ snapshot }">
            <SummaryRejectMark v-if="snapshot.status !== 'user'" :key="rejectMarkKey" :snapshot="snapshot" />
            <StringTooltip
              v-if="'text' in snapshot && snapshot.text.length"
              align="end"
              side="bottom"
              item="Show/Hide summary text"
            >
              <CollapsibleTrigger
                class="tw-mx-1 data-[state=open]:tw-bg-gray-200 data-[state=open]:tw-text-gray-700"
                @click.stop
              >
                <i class="fa fa-text-width"></i>
              </CollapsibleTrigger>
            </StringTooltip>
          </template>
        </SummaryItem>
      </VList>
      <button
        v-if="isSelectedOutOfView"
        type="button"
        class="tw-fixed tw-left-1/2 tw-top-10 tw-z-[1000] tw-flex -tw-translate-x-1/2 tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-bg-gray-800 tw-p-2 tw-text-white tw-shadow"
        @click="() => scrollToSelected('center')"
      >
        Jump to selected
      </button>
    </div>
  </div>
</template>
<style>
.bg-user {
  background: #ffff00;
}
.bg-candidate {
  background: #f7941d;
}
.border-3 {
  border-width: 3px;
}
.summary-container {
  font-size: 0.8rem;
}
.bg-transcript {
  background: hsl(60, 100%, 88%);
}
</style>
